import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Speaker, CheckCircle, Circle, Clock, ArrowLeft, ThumbsUp, Plus, X, Send, MessageSquare, Filter } from 'lucide-react';
import {
  getAllFeatureRequests,
  createFeatureRequest,
  voteForFeatureRequest,
  hasVotedForFeature,
  getCategoryLabel,
  getStatusLabel,
  FeatureRequest,
} from '../../services/featureRequests';
import toast from 'react-hot-toast';

export default function RoadmapPage() {
  const [featureRequests, setFeatureRequests] = useState<FeatureRequest[]>([]);
  const [showRequestForm, setShowRequestForm] = useState(false);
  const [newRequestTitle, setNewRequestTitle] = useState('');
  const [newRequestDescription, setNewRequestDescription] = useState('');
  const [newRequestCategory, setNewRequestCategory] = useState<FeatureRequest['category']>('other');
  const [submitterName, setSubmitterName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [votedFeatures, setVotedFeatures] = useState<Set<string>>(new Set());
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterCategory, setFilterCategory] = useState<string>('all');

  // Generate a simple user ID for voting (stored in localStorage)
  const getUserId = () => {
    let userId = localStorage.getItem('votebeats_roadmap_user_id');
    if (!userId) {
      userId = `user_${Date.now()}_${Math.random().toString(36).substring(2, 8)}`;
      localStorage.setItem('votebeats_roadmap_user_id', userId);
    }
    return userId;
  };

  const userId = getUserId();

  // Load feature requests and voted state
  useEffect(() => {
    async function loadData() {
      const requests = await getAllFeatureRequests();
      setFeatureRequests(requests);

      // Check which features user has voted for
      const voted = new Set<string>();
      const voteChecks = await Promise.all(
        requests.map(async (req) => ({
          id: req.id,
          hasVoted: await hasVotedForFeature(userId, req.id),
        }))
      );
      voteChecks.forEach(({ id, hasVoted: v }) => {
        if (v) voted.add(id);
      });
      setVotedFeatures(voted);
    }
    loadData();
  }, [userId]);

  const handleSubmitRequest = async () => {
    if (!newRequestTitle.trim()) {
      toast.error('Please enter a title for your request');
      return;
    }
    if (!newRequestDescription.trim()) {
      toast.error('Please describe your feature request');
      return;
    }

    setIsSubmitting(true);
    try {
      const newRequest = await createFeatureRequest({
        title: newRequestTitle.trim(),
        description: newRequestDescription.trim(),
        category: newRequestCategory,
        submittedBy: userId,
        submittedByName: submitterName.trim() || 'Anonymous',
      });

      setFeatureRequests(prev => [...prev, newRequest]);
      setNewRequestTitle('');
      setNewRequestDescription('');
      setNewRequestCategory('other');
      setShowRequestForm(false);
      toast.success('Feature request submitted!');
    } catch {
      toast.error('Failed to submit request');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVote = async (featureId: string) => {
    try {
      const result = await voteForFeatureRequest(userId, featureId);

      // Update local state
      setFeatureRequests(prev =>
        prev.map(req =>
          req.id === featureId ? { ...req, voteCount: result.voteCount } : req
        )
      );

      // Update voted state
      setVotedFeatures(prev => {
        const newSet = new Set(prev);
        if (result.hasVoted) {
          newSet.add(featureId);
        } else {
          newSet.delete(featureId);
        }
        return newSet;
      });

      toast.success(result.hasVoted ? 'Vote added!' : 'Vote removed');
    } catch {
      toast.error('Failed to vote');
    }
  };

  // Filter feature requests
  const filteredRequests = featureRequests.filter(req => {
    if (filterStatus !== 'all' && req.status !== filterStatus) return false;
    if (filterCategory !== 'all' && req.category !== filterCategory) return false;
    return true;
  }).sort((a, b) => b.voteCount - a.voteCount);

  const roadmapItems = [
    {
      status: 'completed',
      version: '1.0',
      title: 'Core Features',
      items: [
        'DJ authentication and account management',
        'Event creation and configuration',
        'Basic song request system',
        'Real-time voting',
        'Queue management',
      ],
    },
    {
      status: 'in-progress',
      version: '1.1',
      title: 'Enhanced Experience',
      items: [
        'Spotify integration for song search',
        'Tip jar for song requests',
        'DJ messaging system',
        'Event analytics dashboard',
        'Mobile-optimized attendee view',
      ],
    },
    {
      status: 'planned',
      version: '1.2',
      title: 'Advanced Features',
      items: [
        'Pre-made setlists',
        'Blocked songs list',
        'VIP attendee features',
        'Multi-DJ collaboration',
        'White-label customization',
      ],
    },
    {
      status: 'planned',
      version: '2.0',
      title: 'Platform Expansion',
      items: [
        'Mobile apps (iOS & Android)',
        'Hardware integration (DJ controllers)',
        'AI-powered song recommendations',
        'Event history and statistics',
        'Social sharing features',
      ],
    },
  ];

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-6 h-6 text-success" />;
      case 'in-progress':
        return <Clock className="w-6 h-6 text-warning" />;
      default:
        return <Circle className="w-6 h-6 text-gray-400" />;
    }
  };

  const getRoadmapStatusLabel = (status: string) => {
    switch (status) {
      case 'completed':
        return 'Completed';
      case 'in-progress':
        return 'In Progress';
      default:
        return 'Planned';
    }
  };

  const getStatusColor = (status: FeatureRequest['status']) => {
    switch (status) {
      case 'completed':
        return 'bg-success/10 text-success';
      case 'in-progress':
        return 'bg-warning/10 text-warning';
      case 'planned':
        return 'bg-primary/10 text-primary';
      case 'under-review':
        return 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400';
      case 'declined':
        return 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400';
      default:
        return 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400';
    }
  };

  return (
    <div className="min-h-screen bg-background-light dark:bg-background-dark">
      {/* Header */}
      <header className="border-b border-gray-200 dark:border-gray-800">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Speaker className="w-8 h-8 text-primary" />
              <span className="text-xl font-bold text-gray-900 dark:text-white">VoteBeats</span>
            </div>
            <Link
              to="/"
              className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 hover:text-primary"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Link>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <div className="text-center mb-12">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
            Product Roadmap
          </h1>
          <p className="text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
            See what we're building next. Our roadmap is driven by DJ feedback
            and community requests.
          </p>
        </div>

        {/* Roadmap Timeline */}
        <div className="space-y-8 mb-16">
          {roadmapItems.map((milestone, index) => (
            <div key={milestone.version} className="relative">
              {/* Timeline line */}
              {index < roadmapItems.length - 1 && (
                <div className="absolute left-3 top-12 bottom-0 w-0.5 bg-gray-200 dark:bg-gray-700" />
              )}

              <div className="flex gap-6">
                {/* Status icon */}
                <div className="flex-shrink-0">
                  {getStatusIcon(milestone.status)}
                </div>

                {/* Content */}
                <div className="flex-1 pb-8">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="px-2 py-1 text-xs font-medium bg-primary/10 text-primary rounded">
                      v{milestone.version}
                    </span>
                    <span
                      className={`px-2 py-1 text-xs font-medium rounded ${
                        milestone.status === 'completed'
                          ? 'bg-success/10 text-success'
                          : milestone.status === 'in-progress'
                          ? 'bg-warning/10 text-warning'
                          : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400'
                      }`}
                    >
                      {getRoadmapStatusLabel(milestone.status)}
                    </span>
                  </div>

                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
                    {milestone.title}
                  </h2>

                  <ul className="space-y-2">
                    {milestone.items.map((item, itemIndex) => (
                      <li
                        key={itemIndex}
                        className="flex items-center gap-2 text-gray-600 dark:text-gray-400"
                      >
                        <div
                          className={`w-1.5 h-1.5 rounded-full ${
                            milestone.status === 'completed'
                              ? 'bg-success'
                              : 'bg-gray-400'
                          }`}
                        />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Feature Requests Section */}
        <div className="border-t border-gray-200 dark:border-gray-800 pt-12">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
                <MessageSquare className="w-6 h-6 text-primary" />
                Community Feature Requests
              </h2>
              <p className="text-gray-600 dark:text-gray-400">
                Submit and vote on features you'd like to see in VoteBeats.
              </p>
            </div>
            <button
              onClick={() => setShowRequestForm(true)}
              className="btn btn-primary flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Submit Request
            </button>
          </div>

          {/* Filters */}
          <div className="flex flex-wrap gap-4 mb-6">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-gray-500" />
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="input text-sm py-1.5"
              >
                <option value="all">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="under-review">Under Review</option>
                <option value="planned">Planned</option>
                <option value="in-progress">In Progress</option>
                <option value="completed">Completed</option>
                <option value="declined">Declined</option>
              </select>
            </div>
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="input text-sm py-1.5"
            >
              <option value="all">All Categories</option>
              <option value="core">Core Features</option>
              <option value="ux">User Experience</option>
              <option value="integration">Integrations</option>
              <option value="mobile">Mobile</option>
              <option value="other">Other</option>
            </select>
          </div>

          {/* Feature Request List */}
          <div className="space-y-4">
            {filteredRequests.length === 0 ? (
              <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                No feature requests found. Be the first to submit one!
              </div>
            ) : (
              filteredRequests.map((request) => (
                <div
                  key={request.id}
                  className="card p-4 flex gap-4"
                >
                  {/* Vote button */}
                  <div className="flex flex-col items-center">
                    <button
                      onClick={() => handleVote(request.id)}
                      className={`p-2 rounded-lg transition-colors ${
                        votedFeatures.has(request.id)
                          ? 'bg-primary text-white'
                          : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-primary/10 hover:text-primary'
                      }`}
                      title={votedFeatures.has(request.id) ? 'Remove vote' : 'Vote for this feature'}
                    >
                      <ThumbsUp className="w-5 h-5" />
                    </button>
                    <span className="mt-1 text-sm font-medium text-gray-900 dark:text-white">
                      {request.voteCount}
                    </span>
                  </div>

                  {/* Content */}
                  <div className="flex-1">
                    <div className="flex items-start justify-between gap-4">
                      <h3 className="font-semibold text-gray-900 dark:text-white">
                        {request.title}
                      </h3>
                      <span className={`px-2 py-0.5 text-xs font-medium rounded whitespace-nowrap ${getStatusColor(request.status)}`}>
                        {getStatusLabel(request.status)}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                      {request.description}
                    </p>
                    <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">
                      <span className="px-2 py-0.5 bg-gray-100 dark:bg-gray-800 rounded">
                        {getCategoryLabel(request.category)}
                      </span>
                      <span>by {request.submittedByName || 'Anonymous'}</span>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Submit Request Modal */}
        {showRequestForm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="card p-6 w-full max-w-md">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Submit Feature Request
                </h3>
                <button
                  onClick={() => setShowRequestForm(false)}
                  className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Your Name (optional)
                  </label>
                  <input
                    type="text"
                    value={submitterName}
                    onChange={(e) => setSubmitterName(e.target.value)}
                    placeholder="Anonymous"
                    className="input w-full"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Feature Title *
                  </label>
                  <input
                    type="text"
                    value={newRequestTitle}
                    onChange={(e) => setNewRequestTitle(e.target.value)}
                    placeholder="e.g., Add SoundCloud integration"
                    className="input w-full"
                    maxLength={100}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Description *
                  </label>
                  <textarea
                    value={newRequestDescription}
                    onChange={(e) => setNewRequestDescription(e.target.value)}
                    placeholder="Describe the feature and why it would be useful..."
                    className="input w-full h-24 resize-none"
                    maxLength={500}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Category
                  </label>
                  <select
                    value={newRequestCategory}
                    onChange={(e) => setNewRequestCategory(e.target.value as FeatureRequest['category'])}
                    className="input w-full"
                  >
                    <option value="core">Core Features</option>
                    <option value="ux">User Experience</option>
                    <option value="integration">Integrations</option>
                    <option value="mobile">Mobile</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div className="flex gap-3 pt-2">
                  <button
                    onClick={() => setShowRequestForm(false)}
                    className="flex-1 btn btn-secondary"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSubmitRequest}
                    disabled={isSubmitting}
                    className="flex-1 btn btn-primary flex items-center justify-center gap-2"
                  >
                    {isSubmitting ? (
                      'Submitting...'
                    ) : (
                      <>
                        <Send className="w-4 h-4" />
                        Submit
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
